import type { StaffMe } from '@/types/wire';

/**
 * The staff principal (no staff scope exists in @kk/domain yet: TokenProvider.scope is parent | child and
 * AuthSession is Parent | Child — research-stack.md §3.1). Persisted whole under STAFF_SESSION_KEYS.session.
 */
export interface StaffSession {
  scope: 'staff';
  accessToken: string;
  expiresAt: string;
  user: StaffMe['user'];
  memberships: StaffMe['memberships'];
  /**
   * Kiddie Kredit's own staff, who get the dark admin rail and O06. **Absent rather than false for everyone
   * else**, so the word never travels to a club's portal. It lives on the SESSION because a super admin may
   * hold no membership at all — the bootstrap list needs no account row — so every gate that asks "may they be
   * here" must read this before it reads an `orgId` (preconditions Corollary A-5).
   */
  superAdmin?: boolean;
  /** On the Quest Jr. content team (the Team page): the role that opens admin.kiddiekredit.com. */
  contentRole?: 'owner' | 'publisher' | 'writer' | 'reviewer' | 'iconDesigner' | 'viewer';
  /** Invited to the content team, and the address is not verified yet: the admin confirms it by code. */
  contentInvite?: 'unverified';
  /** A company account signed in with no role yet: "Ask an owner to add you" (2026-09-26). */
  contentRequest?: 'waiting';
}

export const STAFF_SESSION_KEYS = {
  session: 'kk.staffSession',
  lastEmail: 'kk.lastEmail',
  /** The invite token from an email link, parked across the Auth0 redirect (the fragment does not survive it). */
  pendingInvite: 'kk.pendingInvite',
  /** Set when a screen sends someone to Auth0 on their behalf; see `automaticSignIn.ts`. */
  automaticSignIn: 'kk.automaticSignIn',
  /** Our own sign-in's session (EmailStaffAuthClient): the tokens, apart from the staff session derived from them. */
  identity: 'kk.staffIdentity',
  /** This browser's own random id: the per-device code limit, and the computer a session is bound to. */
  deviceId: 'kk.deviceId',
  /** Whether a Google sign-in in flight asked for a company account; parked across the redirect. */
  googleIntent: 'kk.googleIntent',
} as const;

/**
 * Why there is no session, when "signed out" is not the whole truth. `notStaff` is Auth0 saying yes and
 * this API saying no — a parent at the club portal — and `failed` is anything else that broke. Both get
 * a screen that says what happened; neither is a code shown to a human.
 */
export type SignInProblem = 'notStaff' | 'failed' | 'notCompanyAccount';

/** Live until the access token expires (the same rule as @kk/domain sessionIsLive, for the staff shape). */
export function staffSessionIsLive(session: StaffSession | null, now: Date): boolean {
  return session !== null && Date.parse(session.expiresAt) > now.getTime();
}

export function parseStaffSession(raw: string | null): StaffSession | null {
  if (!raw) return null;
  try {
    const s = JSON.parse(raw) as Partial<StaffSession>;
    if (
      s.scope !== 'staff' ||
      typeof s.accessToken !== 'string' ||
      typeof s.expiresAt !== 'string' ||
      !s.user ||
      !Array.isArray(s.memberships)
    )
      return null;
    return s as StaffSession;
  } catch {
    return null;
  }
}
